/**
 * Parsing and formatting for the date strings a resume carries
 * (`experience.start_date`, `experience.end_date`, `education.graduation_date`).
 *
 * Resume dates are free-form strings end to end (the schema types them as plain
 * `str`), and their source varies: the assistant writes "Jan 2020", an uploaded
 * resume can be parsed into "2020-01", "01/2020" or a bare "2020". So a picker
 * reading an existing value has to accept all of those, while everything it
 * WRITES uses one canonical display form ("Jan 2020", or a bare year when only
 * the year is known) matching the format the assistant is prompted to use.
 */

export const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/** The value written for a role the candidate still holds. */
export const PRESENT = 'Present'

// Bounds for the year the picker can browse to. Wide enough for a long career
// and for an expected graduation a few years out.
export const MIN_YEAR = 1950
export const MAX_YEAR = new Date().getFullYear() + 10

/** Words an existing end date may use for "still there". */
const PRESENT_WORDS = new Set(['present', 'current', 'now', 'ongoing', 'to date', 'today'])

export interface ResumeDateParts {
  /** 4-digit year, or null when the value carries none. */
  year: number | null
  /** 1-12, or null for a year-only (or unparseable) value. */
  month: number | null
  /** True for "Present" and its synonyms. */
  present: boolean
}

const EMPTY: ResumeDateParts = { year: null, month: null, present: false }

/** Month index (1-12) for a written month name, keyed by its first 3 letters. */
function monthFromName(name: string): number | null {
  const key = name.slice(0, 3).toLowerCase()
  const index = MONTH_LABELS.findIndex((label) => label.toLowerCase() === key)
  return index < 0 ? null : index + 1
}

function validMonth(month: number): number | null {
  return month >= 1 && month <= 12 ? month : null
}

/**
 * Read a stored resume date into its parts. An unrecognized string (a typo, or
 * a form nobody writes like "Summer 2019") parses to all-null: the picker then
 * opens on the current year rather than guessing, and the stored text stays put
 * until the user actually picks something.
 */
export function parseResumeDate(value: string | null | undefined): ResumeDateParts {
  const raw = (value ?? '').trim()
  if (!raw) return EMPTY
  if (PRESENT_WORDS.has(raw.toLowerCase())) return { year: null, month: null, present: true }

  // ISO-ish: 2020-01, 2020/01, 2020-01-15
  const iso = raw.match(/^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/)
  if (iso?.[1] && iso[2]) {
    return { year: Number(iso[1]), month: validMonth(Number(iso[2])), present: false }
  }

  // Month-first numeric: 01/2020, 1-2020
  const numeric = raw.match(/^(\d{1,2})[-/](\d{4})$/)
  if (numeric?.[1] && numeric[2]) {
    return { year: Number(numeric[2]), month: validMonth(Number(numeric[1])), present: false }
  }

  // Written month: "Jan 2020", "January 2020", "Sept. 2019", "Jan, 2020"
  const named = raw.match(/^([A-Za-z]{3,9})\.?,?\s+(\d{4})$/)
  if (named?.[1] && named[2]) {
    const month = monthFromName(named[1])
    if (month) return { year: Number(named[2]), month, present: false }
  }

  // Year only: "2020"
  const year = raw.match(/^(\d{4})$/)
  if (year?.[1]) return { year: Number(year[1]), month: null, present: false }

  return EMPTY
}

/**
 * Render parts back to the canonical stored form: "Jan 2020" with a month,
 * "2020" with a year alone, "Present", or "" when nothing is set.
 */
export function formatResumeDate(parts: Partial<ResumeDateParts>): string {
  if (parts.present) return PRESENT
  const { year, month } = parts
  if (year == null) return ''
  if (month == null) return String(year)
  const label = MONTH_LABELS[month - 1]
  return label ? `${label} ${year}` : String(year)
}

/** Keep a browsed year inside the picker's range. */
export function clampYear(year: number): number {
  return Math.min(MAX_YEAR, Math.max(MIN_YEAR, year))
}
